import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';

const CertUpdateModal = ({ isOpen, onClose, changes, onApply }) => {
  // Track which fields are selected to update and show unchanged fields state
  const [selectedFields, setSelectedFields] = useState(
    changes.reduce((acc, change) => {
      // Only track selection state for fields that have changes
      if (change.hasChanged) {
        acc[change.field] = true;
      }
      return acc;
    }, {})
  );
  const [showUnchanged, setShowUnchanged] = useState(false);

  // Sort changes by priority: missing values (red) first, then changed values (green)
  const sortedChanges = [...changes].sort((a, b) => {
    // Helper function to get sort priority
    const getPriority = (change) => {
      if (!change.hasChanged) return 2; // Unchanged (lowest priority)
      if (change.newValue === null || change.newValue === undefined || change.newValue === '') return 0; // Missing value (highest priority)
      return 1; // Changed value
    };
    
    return getPriority(a) - getPriority(b);
  });

  // Count changed fields for "Select All" checkbox state
  const changedFieldsCount = changes.filter(change => change.hasChanged).length;

  // Toggle individual field selection
  const toggleField = (field) => {
    setSelectedFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Toggle all changed fields
  const toggleAll = () => {
    const allSelected = sortedChanges
      .filter(change => change.hasChanged)
      .every(change => selectedFields[change.field]);
    const newValue = !allSelected;
    
    setSelectedFields(
      sortedChanges.reduce((acc, change) => {
        if (change.hasChanged) {
          acc[change.field] = newValue;
        }
        return acc;
      }, {})
    );
  };

  // Format value for display
  const formatValue = (field, value, isNewValue = false, hasChanged = false) => {
    // Format the raw value
    let formattedValue;
    
    // Handle numeric fields (showing 0 for empty values)
    if (['population', 'pop_higher', 'current_population', 'current_pop_higher', 'mintage'].includes(field)) {
      const num = value === null || value === undefined || value === '' ? 0 : value;
      formattedValue = Number(num).toLocaleString();
    }
    else if (['price_guide_value'].includes(field)) {
      const num = value === null || value === undefined || value === '' ? 0 : value;
      formattedValue = `$${Number(num).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    }
    else if (field === 'weight') {
      const num = value === null || value === undefined || value === '' ? 0 : value;
      formattedValue = `${Number(parseFloat(num).toFixed(4)).toString()}g`;
    }
    else if (field === 'diameter') {
      const num = value === null || value === undefined || value === '' ? 0 : value;
      formattedValue = `${Number(parseFloat(num).toFixed(2)).toString()}mm`;
    }
    // Handle non-numeric fields (showing 'None' for empty values)
    else if (value === null || value === undefined || value === '') {
      formattedValue = 'None';
    }
    // Handle all other fields
    else {
      formattedValue = value.toString();
    }

    // Apply color coding
    if (!hasChanged) {
      return <span className="text-gray-500">{formattedValue}</span>;
    } else if (isNewValue) {
      if (value === null || value === undefined || value === '') {
        return <span className="text-red-600 italic">{formattedValue}</span>;
      }
      return <span className="text-green-600">{formattedValue}</span>;
    } else {
      return <span className="text-gray-900">{formattedValue}</span>;
    }
  };

  // Get human-readable field names
  const getFieldLabel = (field) => {
    const labels = {
      variety: 'Variety',
      metal: 'Metal',
      grade: 'Grade',
      ngc_grade: 'Grade',
      mint_mark: 'Mint Mark',
      country: 'Country',
      denomination: 'Denomination',
      year: 'Year',
      edge: 'Edge',
      composition: 'Composition',
      weight: 'Weight',
      diameter: 'Diameter',
      mintage: 'Mintage',
      minor_variety: 'Minor Variety',
      die_variety: 'Die Variety',
      reference_number: 'Reference Number',
      assayer: 'Assayer',
      pcgs_number: 'PCGS Number',
      price_guide_value: 'Price Guide Value',
      current_population: 'Current Population',
      current_pop_higher: 'Current Pop Higher'
    };
    return labels[field] || field.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const handleApply = () => {
    // Filter changes based on selection
    const selectedChanges = changes.filter(change => selectedFields[change.field]);
    onApply(selectedChanges);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 mx-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-screen max-w-[90vw] transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  Review Updates
                </Dialog.Title>

                <div className="mb-4 space-y-2">
                  <p className="text-sm text-gray-500">
                    Below is the current data from the TPG. Select which changes you want to apply.
                  </p>
                </div>

                <div className="mb-4 flex justify-between items-center">
                  <div className="px-4">
                    {changedFieldsCount > 0 && (
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-indigo-600"
                          checked={sortedChanges
                            .filter(change => change.hasChanged)
                            .every(change => selectedFields[change.field])}
                          onChange={toggleAll}
                        />
                        <span className="ml-2 text-sm text-gray-700">Select All Changes</span>
                      </label>
                    )}
                  </div>
                  <button
                    type="button"
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => setShowUnchanged(!showUnchanged)}
                  >
                    {showUnchanged ? 'Hide Unchanged Fields' : 'Show Unchanged Fields'}
                  </button>
                </div>

                <div className="mt-4 border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="w-8 px-4 py-3">
                          <span className="sr-only">Select</span>
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Field
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Current Value
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          New Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedChanges.map((change, index) => {
                        // Skip unchanged fields if they're hidden
                        if (!change.hasChanged && !showUnchanged) return null;
                        
                        return (
                          <tr key={change.field} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${!change.hasChanged ? 'text-gray-500' : ''}`}>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {change.hasChanged && (
                                <input
                                  type="checkbox"
                                  className="form-checkbox h-4 w-4 text-indigo-600"
                                  checked={selectedFields[change.field]}
                                  onChange={() => toggleField(change.field)}
                                />
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {getFieldLabel(change.field)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              {formatValue(change.field, change.oldValue, false, change.hasChanged)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              {formatValue(change.field, change.newValue, true, change.hasChanged)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                    onClick={handleApply}
                  >
                    Apply Selected Changes
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CertUpdateModal;
